import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EnhancedTab } from '@swyftx/aviary/molecules/EnhancedTabs/EnhancedTabs.types';
import { marginService } from '@swyftx/margin';
import { negativeTabValues, positiveTabValues } from '@swyftx/margin/build/consts';
import { MarginOrderDirection, MarginOrderType, ProtectionOrderVariant } from '@swyftx/margin/build/types';

import { Big } from '@shared/safe-big';

import { useMarginProtectionOrderError, useMarginTradePanelStoreUtils } from 'src/lib/margin/hooks/trade';
import { useMarkets } from 'src/lib/markets/hooks/useMarkets';
import { useFormatInputText } from 'src/lib/utils/hooks/useFormatInputText';

type Props = {
  tradeSide: MarginOrderDirection;
  tradeType: MarginOrderType;
  protectionOrderType: ProtectionOrderVariant;
};

const useMarginTradePanelProtectionOrder = ({ tradeSide, tradeType, protectionOrderType }: Props) => {
  const { getAssetByCode } = useMarkets();
  const { t } = useTranslation('margin');
  const { stopLoss, takeProfit, onUpdateTakeProfit, onUpdateStopLoss, price, inversePrice, selectedMarket } =
    useMarginTradePanelStoreUtils({
      tradeSide,
    });

  const onUpdateValue = useCallback(
    (newPrice: string) => {
      switch (protectionOrderType) {
        case ProtectionOrderVariant.TakeProfit:
          onUpdateTakeProfit({ price: newPrice });
          break;
        case ProtectionOrderVariant.StopLoss:
          onUpdateStopLoss({ price: newPrice });
          break;
        default:
          protectionOrderType satisfies never;
      }
    },
    [onUpdateStopLoss, onUpdateTakeProfit, protectionOrderType],
  );

  const onUpdateChecked = useCallback(
    (checked: boolean) => {
      switch (protectionOrderType) {
        case ProtectionOrderVariant.TakeProfit:
          onUpdateTakeProfit({ checked, price: checked ? undefined : '' });
          break;
        case ProtectionOrderVariant.StopLoss:
          onUpdateStopLoss({ checked, price: checked ? undefined : '' });
          break;
        default:
          protectionOrderType satisfies never;
      }
    },
    [onUpdateStopLoss, onUpdateTakeProfit, protectionOrderType],
  );

  const value = useMemo(() => {
    switch (protectionOrderType) {
      case ProtectionOrderVariant.TakeProfit:
        return takeProfit.price;
      case ProtectionOrderVariant.StopLoss:
        return stopLoss.price;
      default:
        protectionOrderType satisfies never;
        return '';
    }
  }, [protectionOrderType, stopLoss, takeProfit]);

  const checked = useMemo(() => {
    switch (protectionOrderType) {
      case ProtectionOrderVariant.TakeProfit:
        return takeProfit.checked;
      case ProtectionOrderVariant.StopLoss:
        return stopLoss.checked;
      default:
        protectionOrderType satisfies never;
        return false;
    }
  }, [protectionOrderType, stopLoss, takeProfit]);

  const title = useMemo(() => {
    switch (protectionOrderType) {
      case ProtectionOrderVariant.TakeProfit:
        return t('protectionOrders.setTakeProfit');
      case ProtectionOrderVariant.StopLoss:
        return t('protectionOrders.setStopLoss');
      default:
        protectionOrderType satisfies never;
        return '';
    }
  }, [protectionOrderType, t]);

  const leadingLabel = useMemo(() => {
    switch (protectionOrderType) {
      case ProtectionOrderVariant.TakeProfit:
        return t('protectionOrders.tpPrice');
      case ProtectionOrderVariant.StopLoss:
        return t('protectionOrders.slPrice');
      default:
        protectionOrderType satisfies never;
        return '';
    }
  }, [protectionOrderType, t]);

  const { error } = useMarginProtectionOrderError({
    value,
    tradeSide,
    tradeType,
    protectionOrderType,
    price: inversePrice,
    selectedMarket,
  });

  const { formattedAmount, onChangeValue, priceScale } = useFormatInputText({
    asset: getAssetByCode('USD'),
    value,
    onChange: onUpdateValue,
    priceScaleOverride: selectedMarket?.precision,
  });

  const tabs = useMemo((): EnhancedTab<string>[] => {
    const { isLong } = marginService.distilMarginInformation(tradeSide, tradeType);
    const isTakeProfit = protectionOrderType === ProtectionOrderVariant.TakeProfit;

    if ((isLong && isTakeProfit) || (!isLong && !isTakeProfit)) {
      return positiveTabValues.map((val) => ({
        value: val.value.toString(),
        title: val.label,
        animateColor: 'success',
      }));
    } else {
      return negativeTabValues.map((val) => ({
        value: val.value.toString(),
        title: val.label,
        animateColor: 'error',
      }));
    }
  }, [protectionOrderType, tradeSide, tradeType]);

  const onChangeTab = useCallback(
    (newValue: string) => {
      const bigValue = Big(Big(value).eq(0) ? price : value)
        .times(Big(1).plus(newValue))
        .toFixed(priceScale);

      onUpdateValue(bigValue.toString());
    },
    [value, price, priceScale, onUpdateValue],
  );

  return {
    onChangeTab,
    tabs,
    onUpdateValue,
    onUpdateChecked,
    value,
    checked,
    title,
    leadingLabel,
    formattedAmount,
    onChangeValue,
    error,
  };
};

export { useMarginTradePanelProtectionOrder };
